/**
 * O popup do WhatsApp Embedded Signup da Meta (G02, §8.2, §12.1).
 *
 * ── O que roda aqui, e o que roda no servidor ─────────────────────────────
 *
 * Só o navegador pode abrir a janela da Meta e receber o resultado dela. É
 * essa a razão de este arquivo existir, e é onde ele para: o que sai daqui é
 * um `code` de poucos minutos e os identificadores da conta.
 *
 * Trocar o `code` por um token permanente é do servidor, porque exige o app
 * secret — que nunca chega ao navegador. Quem faz isso é
 * `apps/api/src/integracoes/whatsapp-embedded-signup.ts`, repassando ao motor.
 *
 * ── As duas metades chegam por caminhos diferentes ────────────────────────
 *
 * E é a parte contraintuitiva. A Meta entrega:
 *
 *   · o `code`, pelo retorno do `FB.login`;
 *   · a conta escolhida (`waba_id`, `business_id`, `phone_number_id`), por
 *     `postMessage` da janela do popup.
 *
 * Nenhuma das duas basta. O `code` sem a conta não diz QUAL número conectar; a
 * conta sem o `code` não autoriza nada. Este módulo junta as duas e só então
 * devolve — para que quem chama receba um resultado inteiro ou um erro, nunca
 * um meio caminho que só falharia lá no motor.
 *
 * A ordem é a documentada pela Meta, e é a que o motor de conversa também
 * assume: a mensagem chega ANTES do retorno do login. Por isso o retorno é o
 * ponto terminal, e é nele que a promessa se resolve. Se um dia a ordem
 * mudasse, o sintoma seria "a conta não veio" — e é exatamente essa a mensagem
 * de erro abaixo, em vez de um `undefined` viajando para a API.
 */

/** O que o popup entrega, e que a API precisa para criar a caixa. */
export interface ResultadoDoSignup {
  /** De uso único e curta duração. Não registrar em log nem em auditoria. */
  readonly code: string;
  /** `business_id` — o Business Manager dono da conta. */
  readonly businessId: string;
  /** `waba_id` — a conta de WhatsApp Business. */
  readonly wabaId: string;
  /** `phone_number_id`, quando o popup o entrega. O motor o descobre sem ele. */
  readonly phoneNumberId?: string;
}

/**
 * A pessoa fechou a janela ou desistiu.
 *
 * Separado de erro de verdade porque a tela reage diferente: desistir não pede
 * mensagem vermelha, e tratar os dois igual ensina a ignorar o aviso.
 */
export class SignupCanceladoError extends Error {
  constructor() {
    super('A conexão foi cancelada na janela da Meta.');
    this.name = 'SignupCanceladoError';
  }
}

/** O ambiente não tem o aplicativo da Meta configurado, ou o SDK não carregou. */
export class SignupIndisponivelError extends Error {
  constructor(motivo: string) {
    super(motivo);
    this.name = 'SignupIndisponivelError';
  }
}

/**
 * As origens de onde a mensagem do popup pode vir.
 *
 * Conferir a origem é o que impede qualquer outra página aberta de mandar um
 * `postMessage` fingindo ser a Meta e nos fazer conectar a conta dela. São
 * duas porque a Meta serve o fluxo pelos dois domínios, conforme a região.
 */
const ORIGENS_DA_META: readonly string[] = ['https://www.facebook.com', 'https://web.facebook.com'];

const URL_DO_SDK = 'https://connect.facebook.net/en_US/sdk.js';

/**
 * A versão do Grafo que o SDK usa.
 *
 * Precisa acompanhar a `WHATSAPP_API_VERSION` do motor de conversa. Divergir
 * não quebra na hora: quebra quando a Meta aposentar a mais antiga das duas, e
 * aí o sintoma aparece num lado só.
 */
const VERSAO_DO_GRAFO = 'v22.0';

interface RespostaDeLogin {
  readonly authResponse?: { readonly code?: string } | null;
}

interface OpcoesDeLogin {
  readonly config_id: string;
  readonly response_type: 'code';
  readonly override_default_response_type: boolean;
  readonly extras: {
    readonly setup: Record<string, never>;
    readonly featureType: string;
    readonly sessionInfoVersion: string;
  };
}

/** Só o que usamos do SDK da Meta. */
export interface SdkDaMeta {
  init(opcoes: {
    appId: string;
    version: string;
    xfbml?: boolean;
    autoLogAppEvents?: boolean;
  }): void;
  login(retorno: (resposta: RespostaDeLogin) => void, opcoes: OpcoesDeLogin): void;
}

declare global {
  interface Window {
    FB?: SdkDaMeta;
    fbAsyncInit?: () => void;
  }
}

/**
 * Uma carga só por aba, guardada aqui.
 *
 * O SDK se registra em `window.FB`, e chamar `init` duas vezes com valores
 * diferentes deixa o segundo ganhando em silêncio. Como o aplicativo é um só
 * na instalação inteira, memorizar a promessa é o que garante que a segunda
 * tentativa de conectar reaproveite a primeira em vez de reinicializar.
 */
let carregamentoDoSdk: Promise<SdkDaMeta> | null = null;

/** Só para o teste: esquece a carga memorizada. */
export function esquecerSdkCarregado(): void {
  carregamentoDoSdk = null;
}

export function carregarSdkDaMeta(appId: string, documento: Document): Promise<SdkDaMeta> {
  carregamentoDoSdk ??= new Promise<SdkDaMeta>((resolver, recusar) => {
    const janela = documento.defaultView;
    if (!janela) {
      recusar(new SignupIndisponivelError('Este documento não tem janela; o SDK não roda.'));
      return;
    }

    janela.fbAsyncInit = (): void => {
      const sdk = janela.FB;
      if (!sdk) {
        recusar(new SignupIndisponivelError('O SDK da Meta carregou sem se registrar.'));
        return;
      }
      // `xfbml: false`: não temos marcação social na página, e varrer o DOM
      // atrás dela seria trabalho para nada em toda tela de administração.
      sdk.init({ appId, version: VERSAO_DO_GRAFO, xfbml: false, autoLogAppEvents: true });
      resolver(sdk);
    };

    const script = documento.createElement('script');
    script.src = URL_DO_SDK;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onerror = (): void => {
      /*
       * Zerar a memória no erro.
       *
       * Sem isto, uma falha de rede na primeira tentativa condenaria a aba
       * inteira: toda tentativa seguinte devolveria a MESMA promessa recusada,
       * e recarregar a página viraria a única saída.
       */
      carregamentoDoSdk = null;
      recusar(
        new SignupIndisponivelError(
          'O SDK da Meta não carregou. Verifique a conexão e se algum bloqueador de ' +
            'conteúdo está barrando connect.facebook.net.',
        ),
      );
    };
    documento.head.appendChild(script);
  });

  return carregamentoDoSdk;
}

interface ContaEscolhida {
  readonly wabaId: string;
  readonly businessId?: string;
  readonly phoneNumberId?: string;
}

/**
 * Abre o popup e devolve o que ele entregou.
 *
 * `sdk` e `janela` são injetáveis porque o SDK da Meta é um script externo que
 * abre uma janela real: sem essa costura, nenhum caso de teste alcançaria a
 * junção das duas metades — que é justamente a parte que pode dar errado.
 */
export async function conectarWhatsAppPelaMeta(opcoes: {
  readonly appId: string;
  readonly configuracao: string;
  readonly sdk?: SdkDaMeta;
  readonly janela?: Window;
}): Promise<ResultadoDoSignup> {
  const janela = opcoes.janela ?? window;
  const sdk = opcoes.sdk ?? (await carregarSdkDaMeta(opcoes.appId, janela.document));

  return new Promise<ResultadoDoSignup>((resolver, recusar) => {
    /*
     * Num objeto, e não em dois `let`.
     *
     * Quem escreve é o ouvinte de mensagem e quem lê é o retorno do login —
     * dois fechamentos diferentes. Com variáveis soltas, o TypeScript perde o
     * estreitamento entre eles e o código precisaria de asserções que escondem
     * exatamente o caso que interessa conferir.
     */
    const estado: { conta: ContaEscolhida | null; erro: string | null } = {
      conta: null,
      erro: null,
    };

    const ouvir = (evento: MessageEvent): void => {
      if (!ORIGENS_DA_META.includes(evento.origin)) return;

      let dados: {
        type?: string;
        event?: string;
        data?: {
          waba_id?: string;
          business_id?: string;
          phone_number_id?: string;
          error_message?: string;
        };
      };
      try {
        // A Meta manda string JSON. Outras bibliotecas mandam objeto pelo mesmo
        // canal, e um `JSON.parse` sem guarda derrubaria o ouvinte no primeiro.
        dados = JSON.parse(String(evento.data)) as typeof dados;
      } catch {
        return;
      }

      if (dados.type !== 'WA_EMBEDDED_SIGNUP') return;

      if (dados.event === 'FINISH' || dados.event === 'FINISH_ONLY_WABA') {
        const wabaId = dados.data?.waba_id;
        if (!wabaId) return;
        estado.conta = {
          wabaId,
          ...(dados.data?.business_id ? { businessId: dados.data.business_id } : {}),
          ...(dados.data?.phone_number_id ? { phoneNumberId: dados.data.phone_number_id } : {}),
        };
      } else if (dados.event === 'ERROR') {
        estado.erro = dados.data?.error_message ?? 'A Meta recusou a conexão sem dizer o motivo.';
      }
      // `CANCEL` não precisa de tratamento: o retorno do login vem sem `code`,
      // e é lá que a desistência é decidida — num lugar só.
    };

    janela.addEventListener('message', ouvir);

    sdk.login(
      (resposta) => {
        janela.removeEventListener('message', ouvir);

        const code = resposta?.authResponse?.code;
        if (!code) {
          recusar(new SignupCanceladoError());
          return;
        }
        if (estado.erro) {
          recusar(new Error(estado.erro));
          return;
        }
        if (!estado.conta) {
          recusar(
            new Error(
              'A Meta autorizou, mas não disse qual conta foi escolhida. Tente de novo; se ' +
                'repetir, é sinal de que o fluxo do aplicativo mudou do lado dela.',
            ),
          );
          return;
        }

        const conta = estado.conta;
        if (!conta.businessId) {
          /*
           * O `business_id` é exigido pela API, e por uma razão que não é
           * burocrática: é ele que diz de qual Business Manager a conta é.
           *
           * Recusar aqui, com o nome do campo, é melhor do que mandar vazio e
           * receber do motor um "conexão recusada" genérico — que mandaria
           * qualquer pessoa investigar a credencial, que está certa.
           */
          recusar(
            new Error(
              'A Meta não informou o Business Manager (`business_id`) da conta escolhida. ' +
                'Sem ele não dá para conectar o número.',
            ),
          );
          return;
        }

        resolver({
          code,
          wabaId: conta.wabaId,
          businessId: conta.businessId,
          ...(conta.phoneNumberId ? { phoneNumberId: conta.phoneNumberId } : {}),
        });
      },
      {
        config_id: opcoes.configuracao,
        /*
         * `code`, e não o token — com a marca que desliga o padrão.
         *
         * O padrão do `FB.login` é devolver um access token AO NAVEGADOR. Seria
         * o token da pessoa que autorizou, ele abre tudo o que ela administra,
         * e passaria pelo histórico e por qualquer extensão instalada. O `code`
         * não vale nada sem o app secret, que só o servidor tem.
         *
         * `override_default_response_type` é obrigatório: sem ele a Meta ignora
         * o `response_type` e manda o token assim mesmo.
         */
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
      },
    );
  });
}
